import { buildVisibleUserMessageText } from "./ChatView.logic";

const ASSISTANT_CHARS_PER_LINE_FALLBACK = 72;
const USER_CHARS_PER_LINE_FALLBACK = 56;
const LINE_HEIGHT_PX = 22;
// User bubbles use text-sm leading-relaxed: 14px with a 1.625 line height.
const USER_LINE_HEIGHT_PX = 14 * 1.625;
const ASSISTANT_BASE_HEIGHT_PX = 78;
const USER_BASE_HEIGHT_PX = 96;
const ATTACHMENTS_PER_ROW = 2;
// Attachment thumbnails render with `max-h-[220px]` plus ~8px row gap.
const USER_ATTACHMENT_ROW_HEIGHT_PX = 228;
const UPLOADED_FILES_PER_ROW = 2;
const USER_UPLOADED_FILE_ROW_HEIGHT_PX = 40;
const USER_BUBBLE_WIDTH_RATIO = 0.8;
const USER_BUBBLE_HORIZONTAL_PADDING_PX = 32;
const ASSISTANT_MESSAGE_HORIZONTAL_PADDING_PX = 8;
const USER_MESSAGE_AVG_CHAR_WIDTH_PX = 7.2;
const ASSISTANT_AVG_CHAR_WIDTH_PX = 7.2;
const MIN_USER_CHARS_PER_LINE = 4;
const MIN_ASSISTANT_CHARS_PER_LINE = 20;

interface TimelineMessageHeightInput {
  role: "user" | "assistant" | "system";
  text: string;
  attachments?: ReadonlyArray<{ id: string }>;
  uploadedFiles?: ReadonlyArray<{ workspacePath: string }>;
}

interface TimelineHeightEstimateLayout {
  timelineWidthPx: number | null;
  /** Override mono/UI avg glyph width when measuring in a real browser. */
  avgCharWidthPx?: number | null;
  /**
   * When set, bypass bubble-ratio/padding heuristics and use the measured
   * text-box width from the rendered user message.
   */
  userTextWidthPx?: number | null;
}

function estimateWrappedLineCount(text: string, charsPerLine: number): number {
  if (text.length === 0) return 1;

  // Avoid allocating via split for long logs; iterate once and count wrapped lines.
  let lines = 0;
  let currentLineLength = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (text.charCodeAt(index) === 10) {
      lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
      currentLineLength = 0;
      continue;
    }
    currentLineLength += 1;
  }

  lines += Math.max(1, Math.ceil(currentLineLength / charsPerLine));
  return lines;
}

function isFinitePositiveNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function resolveAvgCharWidthPx(layout: TimelineHeightEstimateLayout, fallbackPx: number): number {
  return isFinitePositiveNumber(layout.avgCharWidthPx) ? layout.avgCharWidthPx : fallbackPx;
}

function estimateCharsPerLineForUser(
  timelineWidthPx: number | null,
  avgCharWidthPx: number,
  userTextWidthPx?: number | null,
): number {
  if (isFinitePositiveNumber(userTextWidthPx)) {
    return Math.max(MIN_USER_CHARS_PER_LINE, Math.floor(userTextWidthPx / avgCharWidthPx));
  }
  if (!isFinitePositiveNumber(timelineWidthPx)) return USER_CHARS_PER_LINE_FALLBACK;
  const bubbleWidthPx = timelineWidthPx * USER_BUBBLE_WIDTH_RATIO;
  const textWidthPx = Math.max(bubbleWidthPx - USER_BUBBLE_HORIZONTAL_PADDING_PX, 0);
  return Math.max(MIN_USER_CHARS_PER_LINE, Math.floor(textWidthPx / avgCharWidthPx));
}

function estimateCharsPerLineForAssistant(
  timelineWidthPx: number | null,
  avgCharWidthPx: number,
): number {
  if (!isFinitePositiveNumber(timelineWidthPx)) return ASSISTANT_CHARS_PER_LINE_FALLBACK;
  const textWidthPx = Math.max(timelineWidthPx - ASSISTANT_MESSAGE_HORIZONTAL_PADDING_PX, 0);
  return Math.max(MIN_ASSISTANT_CHARS_PER_LINE, Math.floor(textWidthPx / avgCharWidthPx));
}

export function estimateTimelineMessageHeight(
  message: TimelineMessageHeightInput,
  layout: TimelineHeightEstimateLayout = { timelineWidthPx: null },
): number {
  if (message.role === "assistant") {
    const charsPerLine = estimateCharsPerLineForAssistant(
      layout.timelineWidthPx,
      resolveAvgCharWidthPx(layout, ASSISTANT_AVG_CHAR_WIDTH_PX),
    );
    const estimatedLines = estimateWrappedLineCount(message.text, charsPerLine);
    return ASSISTANT_BASE_HEIGHT_PX + estimatedLines * LINE_HEIGHT_PX;
  }

  if (message.role === "user") {
    const visibleUserText = buildVisibleUserMessageText({
      prompt: message.text,
      uploadedFilesCount: message.uploadedFiles?.length ?? 0,
    });
    const charsPerLine = estimateCharsPerLineForUser(
      layout.timelineWidthPx,
      resolveAvgCharWidthPx(layout, USER_MESSAGE_AVG_CHAR_WIDTH_PX),
      layout.userTextWidthPx,
    );
    const estimatedLines = estimateWrappedLineCount(visibleUserText, charsPerLine);
    const attachmentCount = message.attachments?.length ?? 0;
    const attachmentRows = Math.ceil(attachmentCount / ATTACHMENTS_PER_ROW);
    const attachmentHeight = attachmentRows * USER_ATTACHMENT_ROW_HEIGHT_PX;
    const uploadedFileCount = message.uploadedFiles?.length ?? 0;
    const uploadedFileRows = Math.ceil(uploadedFileCount / UPLOADED_FILES_PER_ROW);
    const uploadedFileHeight = uploadedFileRows * USER_UPLOADED_FILE_ROW_HEIGHT_PX;
    return (
      USER_BASE_HEIGHT_PX +
      estimatedLines * USER_LINE_HEIGHT_PX +
      attachmentHeight +
      uploadedFileHeight
    );
  }

  // `system` messages are not rendered in the chat timeline, but keep a stable
  // explicit branch in case they are present in timeline data.
  const charsPerLine = estimateCharsPerLineForAssistant(
    layout.timelineWidthPx,
    resolveAvgCharWidthPx(layout, ASSISTANT_AVG_CHAR_WIDTH_PX),
  );
  const estimatedLines = estimateWrappedLineCount(message.text, charsPerLine);
  return ASSISTANT_BASE_HEIGHT_PX + estimatedLines * LINE_HEIGHT_PX;
}
